export interface BlogMetadata { id: string; slug: string; title: string; excerpt: string; date: string; readTime: string; category: string; author: string; tags: string[]; image: string; imageWidth: number; imageHeight: number; originalUrl?: string; isGuestArticle?: boolean }
import { LAUNCH_DATE, tpchCopy } from '@/data/hardcharger-launch';

const tpchEn = tpchCopy('en');
const tpchPt = tpchCopy('pt-BR');

export const blogMetadata = {
 en: {
  "tpcds-10tb-hardcharger-vs-motherduck": {
    "id": "tpcds-10tb-hardcharger-vs-motherduck",
    "slug": "tpcds-10tb-hardcharger-vs-motherduck",
    "title": "Bigger Ducks, Faster: Hardcharger on GizmoData Cloud vs. MotherDuck at 1 TB and 10 TB (Derived from TPC-DS)",
    "date": "2026-11-03",
    "readTime": "9 min read",
    "category": "Performance",
    "author": "Philip Moore",
    "tags": [
      "Benchmark",
      "TPC-DS",
      "Hardcharger",
      "GizmoData Cloud",
      "MotherDuck",
      "DuckDB 2.0",
      "DuckDB",
      "AWS Graviton5",
      "Performance"
    ],
    "excerpt": "MotherDuck put its biggest instance, the Giga, up against Snowflake, Databricks, Redshift and BigQuery on 99 queries derived from TPC-DS. So a small, self-funded team took the challenge with a product less than two weeks old. On one 192-vCPU Graviton5 machine, Hardcharger on GizmoData Cloud posted a 0.46-second geometric mean at 1 TB and 3.39 seconds at 10 TB: 1.5 and 1.65 times as fast as the Giga, ahead of every system MotherDuck cited, and cheaper for the same work. Every per-query time, the caveats, and what it costs.",
    "image": "/blog/tpcds-10tb-hardcharger-vs-motherduck/hero-v2.jpg",
    "imageWidth": 1600,
    "imageHeight": 900
  },
  "what-the-heck-is-hardcharger": {
    "id": "what-the-heck-is-hardcharger",
    "slug": "what-the-heck-is-hardcharger",
    "title": "What the Heck is Hardcharger?",
    "date": "2026-10-30",
    "readTime": "6 min read",
    "category": "Guest Article",
    "author": "Shawn Gordon",
    "tags": [
      "Guest Article",
      "Hardcharger",
      "Chargewire",
      "DuckDB 2.0",
      "DuckDB",
      "Apache Arrow",
      "GizmoData Cloud",
      "MCP",
      "Open Source"
    ],
    "excerpt": "Shawn Gordon follows up his GizmoSQL article with a look at its successor: Hardcharger, an open-source DuckDB 2.0 server built on Chargewire, an HTTP protocol that streams Apache Arrow and works with any proxy or load balancer. He covers why GizmoData left Arrow Flight SQL, the security built into the protocol, the formerly Enterprise features now included, and sharing a local DuckDB file on GizmoData Cloud that Claude queries over MCP. Originally published on HackerNoon.",
    "originalUrl": "https://hackernoon.com/what-the-heck-is-hardcharger",
    "isGuestArticle": true,
    "image": "/blog/what-the-heck-is-hardcharger/hero-800w.webp",
    "imageWidth": 1168,
    "imageHeight": 784
  },
  "introducing-hardcharger": {
    "id": "introducing-hardcharger",
    "slug": "introducing-hardcharger",
    "title": "Introducing Hardcharger™: The DuckDB 2.0 Server, Built on the Open Chargewire Protocol",
    "date": LAUNCH_DATE,
    "readTime": "12 min read",
    "category": "Release",
    "author": "Philip Moore",
    "tags": [
      "Hardcharger",
      "Chargewire",
      "DuckDB 2.0",
      "DuckDB",
      "Apache Arrow",
      "Open Source",
      "Benchmark",
      "Performance",
      "RBAC",
      "GizmoData Cloud"
    ],
    "excerpt": `Hardcharger is GizmoData's new open-source server for DuckDB 2.0, built on Chargewire, an open protocol for SQL over HTTP with Apache Arrow results. On one Graviton5 machine it ran 22 queries derived from TPC-H over 1 TB in ${tpchEn.hcSeconds} seconds, for about ${tpchEn.hcCents} cents of compute, and every feature is included: access control, SSO, mTLS, audit, instrumentation and a built-in web UI. Apache-2.0, no license keys.`,
    "image": "/blog/introducing-hardcharger/hero-v2-800w.webp",
    "imageWidth": 1600,
    "imageHeight": 900
  },
  "introducing-gizmodata-cloud": {
    "id": "introducing-gizmodata-cloud",
    "slug": "introducing-gizmodata-cloud",
    "title": "Introducing GizmoData Cloud: The Best Way to Run DuckDB in the Cloud",
    "date": "2026-09-28",
    "readTime": "6 min read",
    "category": "Release",
    "author": "Philip Moore",
    "tags": [
      "GizmoData Cloud",
      "GizmoSQL",
      "DuckDB",
      "DuckLake",
      "Arrow Flight SQL",
      "Managed Service",
      "AWS",
      "Google Cloud",
      "Azure"
    ],
    "excerpt": "GizmoData Cloud is now generally available and open to everyone. It is fully managed GizmoSQL: DuckDB-powered Arrow Flight SQL clusters, DuckLake catalogs and federated queries on AWS, Google Cloud and Azure, run from one control plane. Sign up at app.gizmodata.com. The first 20 sign-ups get $25 of credit and no credit card with our launch code.",
    "image": "/gizmodata-cloud-launch-blog.svg",
    "imageWidth": 1600,
    "imageHeight": 900
  },
  "gizmosql-session-limits-idle-timeouts": {
    "id": "gizmosql-session-limits-idle-timeouts",
    "slug": "gizmosql-session-limits-idle-timeouts",
    "title": "The Connection That Never Left: Keeping GizmoSQL Lean With Session Limits and Idle Timeouts",
    "date": "2026-09-15",
    "readTime": "5 min read",
    "category": "Guest Article",
    "author": "Emmanuel Sibanda",
    "tags": [
      "Guest Article",
      "GizmoSQL",
      "Operations",
      "Sessions",
      "Idle Timeout",
      "Memory",
      "DuckDB",
      "Arrow Flight SQL"
    ],
    "excerpt": "Nobody ran a query since Friday, yet Monday morning the GizmoSQL server is at 80% memory. The queries left; the connections didn't. Guest author Emmanuel Sibanda walks through the two flags that fix it: --max-sessions caps how many sessions can exist, and --session-idle-timeout evicts the ones that went quiet, with an idle clock that only real user SQL (including result streaming) can reset.",
    "originalUrl": "https://www.emmanuelsibanda.com",
    "isGuestArticle": true,
    "image": "/gizmosql-idle-sessions-blog.svg",
    "imageWidth": 1600,
    "imageHeight": 900
  },
  "gizmosql-ducklake-elastic-compute-shared-state": {
    "id": "gizmosql-ducklake-elastic-compute-shared-state",
    "slug": "gizmosql-ducklake-elastic-compute-shared-state",
    "title": "GizmoSQL + DuckLake: Elastic Compute Over Shared State",
    "date": "2026-09-11",
    "readTime": "5 min read",
    "category": "Architecture",
    "author": "Chris Wones",
    "tags": [
      "DuckLake",
      "GizmoSQL",
      "Architecture",
      "Autoscaling",
      "Lakehouse",
      "DuckDB",
      "PostgreSQL",
      "Parquet",
      "Kubernetes"
    ],
    "excerpt": "Scaling stateless compute is easy. Scaling independent compute nodes that all need the same mutable data is the real problem in analytics. With DuckLake holding the transactional catalog and the Parquet files in object storage, a GizmoSQL cluster can grow from one node to five and back without copying data, rebuilding metadata, or reconciling state. Strategic Advisor Chris Wones on why the catalog is what makes scaling clean.",
    "image": "/gizmosql-ducklake-elastic-compute-hero.svg",
    "imageWidth": 1600,
    "imageHeight": 900
  },
  "seagull-scalable-web-gis-gizmosql": {
    "id": "seagull-scalable-web-gis-gizmosql",
    "slug": "seagull-scalable-web-gis-gizmosql",
    "title": "Seagull: How to Use GizmoSQL to Build Scalable Web GIS Applications",
    "date": "2026-09-08",
    "readTime": "6 min read",
    "category": "Guest Article",
    "author": "Kasper F. Pedersen",
    "tags": [
      "Guest Article",
      "GIS",
      "Maritime",
      "AIS",
      "DuckDB",
      "GizmoSQL",
      "Arrow Flight SQL",
      "MapLibre",
      "Apache Airflow",
      "Aalborg University"
    ],
    "excerpt": "Guest post from Aalborg University's Daisy research group. Seagull serves 18.7 billion AIS records from 126,400 ships as live map layers, with every vector tile computed on request from DuckDB. PostgreSQL needed a pile of pre-aggregated tables to keep up; DuckDB was fast but single-writer. GizmoSQL turned it into a centralized database that Airflow, MapLibre Martin, and the web app all share over Arrow Flight SQL.",
    "originalUrl": "https://seagull.app.cs.aau.dk",
    "isGuestArticle": true,
    "image": "/seagull-gizmosql-blog.svg",
    "imageWidth": 1600,
    "imageHeight": 900
  },
  "tpch-1tb-graviton5-80-seconds": {
    "id": "tpch-1tb-graviton5-80-seconds",
    "slug": "tpch-1tb-graviton5-80-seconds",
    "title": "TPC-H 1 TB in 80 Seconds: GizmoSQL on AWS Graviton5 (vs. Graviton4 and Azure Cobalt 100)",
    "date": "2026-09-04",
    "readTime": "10 min read",
    "category": "Performance",
    "author": "Philip Moore",
    "tags": [
      "Benchmark",
      "TPC-H",
      "AWS Graviton5",
      "Graviton4",
      "Graviton",
      "Arm",
      "GizmoSQL",
      "DuckDB",
      "Performance"
    ],
    "excerpt": "We ran our 22-query TPC-H 1 TB benchmark on one of the first AWS Graviton5 instances, an r9gd.24xlarge: 80.5 seconds for $0.17 of compute. The same afternoon, same DuckDB, a Graviton4 r8gd.24xlarge took 89.8 seconds and an Azure Cobalt 100 E96pds_v6 took 106.8, so Graviton5 is 10% faster than Graviton4 and 25% faster than Cobalt 100, winning all 22 queries. Full methodology and every per-query timing for all three runs.",
    "image": "/tpch-1tb-graviton5-blog.svg",
    "imageWidth": 1600,
    "imageHeight": 900
  },
  "introducing-gizmosql-mcp-server": {
    "id": "introducing-gizmosql-mcp-server",
    "slug": "introducing-gizmosql-mcp-server",
    "title": "Introducing the GizmoSQL MCP Server: Talk to Your DuckDB Data from Claude",
    "date": "2026-09-04",
    "readTime": "7 min read",
    "category": "Release",
    "author": "Philip Moore",
    "tags": [
      "MCP",
      "Model Context Protocol",
      "Claude",
      "AI Agents",
      "GizmoSQL",
      "DuckDB",
      "Arrow Flight SQL"
    ],
    "excerpt": "We shipped an MCP server and Claude Desktop extension for GizmoSQL. Claude Desktop, Claude Code, and any Model Context Protocol client can now explore your schema and run SQL against a GizmoSQL server — read-only by default, with row caps and timeouts the server enforces, and credentials that never leave your machine.",
    "image": "/gizmosql-mcp-blog.svg",
    "imageWidth": 1600,
    "imageHeight": 900
  },
  "gizmodata-adbc-driver-suite": {
    "id": "gizmodata-adbc-driver-suite",
    "slug": "gizmodata-adbc-driver-suite",
    "title": "Meet the GizmoData ADBC Driver Suite: Move Data Between Oracle, Db2, and GizmoSQL",
    "date": "2026-08-27",
    "readTime": "9 min read",
    "category": "Release",
    "author": "Philip Moore",
    "tags": [
      "ADBC",
      "Oracle",
      "Db2",
      "GizmoSQL",
      "DuckDB",
      "Apache Arrow",
      "Go",
      "Data Integration"
    ],
    "excerpt": "We built pure-Go, pip-installable ADBC drivers for Oracle (no Instant Client), IBM Db2 (no CLI/JCC), and DuckDB's Quack — alongside our flagship GizmoSQL driver. Because they're all Arrow-native, moving a table from Oracle or Db2 into GizmoSQL is a few lines of Python, or a single SQL statement from DuckDB — in both directions.",
    "image": "/adbc-drivers-launch-blog.svg",
    "imageWidth": 1600,
    "imageHeight": 900
  },
  "gizmosql-bulk-ingest-arrow-scan-rewrite": {
    "id": "gizmosql-bulk-ingest-arrow-scan-rewrite",
    "slug": "gizmosql-bulk-ingest-arrow-scan-rewrite",
    "title": "The Arrow Scan Rewrite: How GizmoSQL v1.37.0 Made Bulk Ingest 7x Faster",
    "date": "2026-08-24",
    "readTime": "10 min read",
    "category": "Guest Article",
    "author": "Chris Harrison",
    "tags": [
      "GizmoSQL",
      "Bulk Ingestion",
      "Arrow Flight SQL",
      "DuckDB",
      "ADBC",
      "Performance",
      "Apache Arrow"
    ],
    "excerpt": "GizmoSQL v1.37.0 stopped hand-converting every Arrow cell on the way into DuckDB and started streaming the whole batch through DuckDB's own arrow_scan instead — 7.2x faster bulk loads, exact decimals, and geometry columns that finally land as GEOMETRY. A commit walkthrough, plus a runnable demo repo to reproduce the numbers yourself.",
    "originalUrl": "https://github.com/virtualninjach/bulk-ingest-demo",
    "isGuestArticle": true,
    "image": "/gizmosql-bulk-ingest-arrow-scan-blog.svg",
    "imageWidth": 1600,
    "imageHeight": 900
  },
  "gizmosql-adbc-v2-go-driver": {
    "id": "gizmosql-adbc-v2-go-driver",
    "slug": "gizmosql-adbc-v2-go-driver",
    "title": "GizmoSQL ADBC Driver 2.0: A Go-Powered Core That Brings GizmoSQL to Every ADBC Language",
    "date": "2026-07-29",
    "readTime": "8 min read",
    "category": "Release",
    "author": "Philip Moore",
    "tags": [
      "ADBC",
      "Go",
      "Python",
      "Arrow Flight SQL",
      "GizmoSQL",
      "OAuth",
      "Release"
    ],
    "excerpt": "The GizmoSQL ADBC driver has been rebuilt from the ground up in Go — with a thin Python wrapper that stays drop-in compatible with 1.x. One native driver now brings OAuth/SSO, automatic DDL/DML detection, and the rest of the GizmoSQL goodies to Python, Go, R, Rust, C#, C/C++, and JavaScript.",
    "image": "/gizmosql-adbc-v2-blog.svg",
    "imageWidth": 1600,
    "imageHeight": 900
  },
  "welcoming-quack-duckdb-client-server": {
    "id": "welcoming-quack-duckdb-client-server",
    "slug": "welcoming-quack-duckdb-client-server",
    "title": "Welcoming Quack: DuckDB's New Client-Server Protocol — and Where GizmoSQL Goes From Here",
    "date": "2026-05-13",
    "readTime": "6 min read",
    "category": "Commentary",
    "author": "Philip Moore",
    "tags": [
      "GizmoSQL",
      "DuckDB",
      "Quack",
      "Arrow Flight SQL",
      "Client-Server"
    ],
    "excerpt": "DuckDB Labs announced Quack, a native client-server protocol for DuckDB. A thank-you to the team for the kind acknowledgement — and a look at where GizmoSQL fits in a world with multiple DuckDB server protocols.",
    "image": "/welcoming-quack-blog.webp",
    "imageWidth": 1600,
    "imageHeight": 800
  },
  "freeing-individuals-from-the-matrix": {
    "id": "freeing-individuals-from-the-matrix",
    "slug": "freeing-individuals-from-the-matrix",
    "title": "Freeing the Individuals from the Matrix: How GizmoSQL Makes Your Database AI-Agent Ready",
    "date": "2026-03-05",
    "readTime": "15 min read",
    "category": "Guest Article",
    "author": "Chris Harrison",
    "tags": [
      "GizmoSQL",
      "AI Agents",
      "DuckDB",
      "Claude",
      "Apache Arrow",
      "Python",
      "Windows"
    ],
    "excerpt": "A Matrix-themed deep dive into building an AI-agent-ready architecture with GizmoSQL, DuckDB, and Claude. Simulates one million people, mutates them with automated agents, and monitors anomalies with an autonomous AI—all over Arrow Flight SQL.",
    "originalUrl": "https://github.com/virtualninjach/gizmosql-matrix",
    "isGuestArticle": true,
    "image": "/gizmosql-matrix-blog.webp",
    "imageWidth": 1536,
    "imageHeight": 1024
  },
  "what-the-heck-is-gizmosql": {
    "id": "what-the-heck-is-gizmosql",
    "slug": "what-the-heck-is-gizmosql",
    "title": "What the Heck is GizmoSQL?",
    "date": "2025-12-30",
    "readTime": "6 min read",
    "category": "Guest Article",
    "author": "Shawn Gordon",
    "tags": [
      "GizmoSQL",
      "DuckDB",
      "Apache Arrow",
      "Arrow Flight SQL",
      "Open Source"
    ],
    "excerpt": "A deep dive into GizmoSQL—a small server that runs DuckDB with the Arrow Flight SQL protocol wrapped around it so you can run DuckDB remotely. Originally published on HackerNoon.",
    "originalUrl": "https://hackernoon.com/what-the-heck-is-gizmosql",
    "isGuestArticle": true,
    "image": "/what-the-heck-is-gizmosql-blog.webp",
    "imageWidth": 1280,
    "imageHeight": 1211
  },
  "introducing-gizmosql-operator": {
    "id": "introducing-gizmosql-operator",
    "slug": "introducing-gizmosql-operator",
    "title": "Introducing GizmoSQL Operator",
    "date": "2025-12-19",
    "readTime": "5 min read",
    "category": "Guest Article",
    "author": "Nima Khoshini",
    "tags": [
      "GizmoSQL",
      "Kubernetes",
      "Operator",
      "Helm",
      "DevOps",
      "Open Source"
    ],
    "excerpt": "Announcing the GizmoSQL Kubernetes Operator—a tool designed to reduce the operational cost of running large-scale SQL queries with reported savings of 90% compared to competing cloud data engines.",
    "originalUrl": "https://www.nima.blog/blog/introducing-gizmosql-operator",
    "isGuestArticle": true,
    "image": "/gizmosql-operator-blog.webp",
    "imageWidth": 1024,
    "imageHeight": 1024
  },
  "adbc-flightsql-bulk-ingestion": {
    "id": "adbc-flightsql-bulk-ingestion",
    "slug": "adbc-flightsql-bulk-ingestion",
    "title": "ADBC Flight SQL 1.10.0: Bulk Ingestion for GizmoSQL",
    "date": "2026-01-10",
    "readTime": "6 min read",
    "category": "Release",
    "author": "GizmoData Team",
    "tags": [
      "ADBC",
      "Arrow Flight SQL",
      "GizmoSQL",
      "Python",
      "DuckDB",
      "Bulk Ingestion"
    ],
    "excerpt": "The new ADBC Flight SQL Python driver 1.10.0 brings bulk ingestion to Arrow Flight SQL servers like GizmoSQL. Load millions of rows directly from Python or DuckDB with automatic schema inference.",
    "image": "/adbc-bulk-ingestion-blog.webp",
    "imageWidth": 1536,
    "imageHeight": 1024
  },
  "gizmoedge-one-trillion-row-challenge": {
    "id": "gizmoedge-one-trillion-row-challenge",
    "slug": "gizmoedge-one-trillion-row-challenge",
    "title": "GizmoEdge Takes on the 1 Trillion Row Challenge",
    "date": "2025-10-22",
    "readTime": "8 min read",
    "category": "Performance",
    "author": "GizmoData Team",
    "tags": [
      "GizmoEdge",
      "DuckDB",
      "Big Data",
      "Performance",
      "Distributed Computing"
    ],
    "excerpt": "What happens when you give a distributed SQL engine a trillion-row dataset? GizmoEdge crushed the Coiled challenge with a 1,000-worker cluster on Azure.",
    "image": "/gizmoedge-1trc-blog.webp",
    "imageWidth": 1536,
    "imageHeight": 1024
  },
  "gizmosql-one-trillion-row-challenge": {
    "id": "gizmosql-one-trillion-row-challenge",
    "slug": "gizmosql-one-trillion-row-challenge",
    "title": "GizmoSQL One-Trillion Row Challenge Results",
    "date": "2025-07-15",
    "readTime": "5 min read",
    "category": "Performance",
    "author": "GizmoData Team",
    "tags": [
      "GizmoSQL",
      "DuckDB",
      "Big Data",
      "Performance"
    ],
    "excerpt": "GizmoSQL completed the 1 trillion row challenge! Learn how we processed massive datasets using DuckDB and Apache Arrow Flight SQL.",
    "image": "/gizmosql-1trc-blog.webp",
    "imageWidth": 1536,
    "imageHeight": 1024
  },
  "adbc-replicate-snowflake-oracle-ai-agents": {
    "id": "adbc-replicate-snowflake-oracle-ai-agents",
    "slug": "adbc-replicate-snowflake-oracle-ai-agents",
    "title": "Replicate Once, Query All Day: adbc-replicate Feeds AI Agents from Snowflake and Oracle",
    "date": "2026-10-16",
    "readTime": "11 min read",
    "category": "Release",
    "author": "Philip Moore",
    "tags": [
      "adbc-replicate",
      "Replication",
      "Snowflake",
      "Oracle",
      "MCP",
      "AI Agents",
      "GizmoData Cloud",
      "GizmoSQL",
      "DuckDB",
      "Apache Arrow",
      "ADBC"
    ],
    "excerpt": "adbc-replicate is our new open-source tool for copying whole schemas from Snowflake, Oracle, PostgreSQL or DuckDB into GizmoSQL. Data streams as Arrow from end to end, masks run inside the source, and each run copies only what changed. Two customers, one on Snowflake and one on Oracle, run it every hour into GizmoData Cloud, and their AI agents query the copy over MCP all day without touching the source.",
    "image": "/adbc-replicate-blog.svg",
    "imageWidth": 1600,
    "imageHeight": 900
  },
  "tpch-1tb-horse-race-aws-gcp-azure": {
    "id": "tpch-1tb-horse-race-aws-gcp-azure",
    "slug": "tpch-1tb-horse-race-aws-gcp-azure",
    "title": "Three Clouds, One API: A 1 TB TPC-H Horse Race on GizmoData Cloud (AWS vs. Google Cloud vs. Azure)",
    "date": "2026-10-08",
    "readTime": "9 min read",
    "category": "Performance",
    "author": "Philip Moore",
    "tags": [
      "Benchmark",
      "TPC-H",
      "GizmoData Cloud",
      "AWS Graviton5",
      "Google Axion",
      "Azure Cobalt 100",
      "DuckLake",
      "DuckDB 2.0",
      "GizmoSQL",
      "DuckDB",
      "Performance"
    ],
    "excerpt": "We raced six 64-vCPU GizmoSQL clusters on AWS, Google Cloud and Azure over 1 TB of TPC-H, on local NVMe and on DuckLake, with DuckDB 1.5.6 and the DuckDB 2.0 alpha. AWS Graviton5 ran the fastest lap (79 seconds for all 22 queries, $0.30), Google Cloud took four of six golds, and DuckDB 2.0 beat 1.5.6 in every lane. Twelve results with a price on every one, in 38 minutes and about $37 on GizmoData Cloud.",
    "image": "/blog/tpch-1tb-horse-race/hero-800w.webp",
    "imageWidth": 1600,
    "imageHeight": 901
  }
},
 'pt-BR': {
  "tpcds-10tb-hardcharger-vs-motherduck": {
    "id": "tpcds-10tb-hardcharger-vs-motherduck",
    "slug": "tpcds-10tb-hardcharger-vs-motherduck",
    "title": "Patos maiores, mais rápidos: Hardcharger no GizmoData Cloud vs. MotherDuck em 1 TB e 10 TB (derivado do TPC-DS)",
    "date": "2026-11-03",
    "readTime": "9 min de leitura",
    "category": "Desempenho",
    "author": "Philip Moore",
    "tags": [
      "Benchmark",
      "TPC-DS",
      "Hardcharger",
      "GizmoData Cloud",
      "MotherDuck",
      "DuckDB 2.0",
      "DuckDB",
      "AWS Graviton5",
      "Desempenho"
    ],
    "excerpt": "A MotherDuck colocou sua maior instância, a Giga, contra Snowflake, Databricks, Redshift e BigQuery em 99 consultas derivadas do TPC-DS. Então uma equipe pequena, que se financia sozinha, aceitou o desafio com um produto de menos de duas semanas de vida. Em uma única máquina Graviton5 de 192 vCPUs, o Hardcharger no GizmoData Cloud registrou média geométrica de 0,46 segundo em 1 TB e 3,39 segundos em 10 TB: 1,5 e 1,65 vez mais rápido que o Giga, à frente de todos os sistemas citados pela MotherDuck, e mais barato para o mesmo trabalho. Todos os tempos por consulta, as ressalvas e quanto custa.",
    "image": "/blog/tpcds-10tb-hardcharger-vs-motherduck/hero-v2.jpg",
    "imageWidth": 1600,
    "imageHeight": 900
  },
  "what-the-heck-is-hardcharger": {
    "id": "what-the-heck-is-hardcharger",
    "slug": "what-the-heck-is-hardcharger",
    "title": "Afinal, o que é o Hardcharger?",
    "date": "2026-10-30",
    "readTime": "6 min de leitura",
    "category": "Artigo de convidado",
    "author": "Shawn Gordon",
    "tags": [
      "Artigo de convidado",
      "Hardcharger",
      "Chargewire",
      "DuckDB 2.0",
      "DuckDB",
      "Apache Arrow",
      "GizmoData Cloud",
      "MCP",
      "Código aberto"
    ],
    "excerpt": "Shawn Gordon dá continuidade ao seu artigo sobre o GizmoSQL com uma análise do sucessor: o Hardcharger, um servidor de código aberto para o DuckDB 2.0 construído sobre o Chargewire, um protocolo HTTP que transmite Apache Arrow e funciona com qualquer proxy ou balanceador de carga. Ele explica por que a GizmoData deixou o Arrow Flight SQL, a segurança embutida no protocolo, os recursos antes Enterprise agora incluídos e o compartilhamento de um arquivo DuckDB local no GizmoData Cloud que o Claude consulta via MCP. Publicado originalmente no HackerNoon.",
    "originalUrl": "https://hackernoon.com/what-the-heck-is-hardcharger",
    "isGuestArticle": true,
    "image": "/blog/what-the-heck-is-hardcharger/hero-800w.webp",
    "imageWidth": 1168,
    "imageHeight": 784
  },
  "introducing-hardcharger": {
    "id": "introducing-hardcharger",
    "slug": "introducing-hardcharger",
    "title": "Apresentamos o Hardcharger™: o servidor do DuckDB 2.0, construído sobre o protocolo aberto Chargewire",
    "date": LAUNCH_DATE,
    "readTime": "12 min de leitura",
    "category": "Lançamento",
    "author": "Philip Moore",
    "tags": [
      "Hardcharger",
      "Chargewire",
      "DuckDB 2.0",
      "DuckDB",
      "Apache Arrow",
      "Código aberto",
      "Benchmark",
      "Desempenho",
      "RBAC",
      "GizmoData Cloud"
    ],
    "excerpt": `O Hardcharger é o novo servidor de código aberto da GizmoData para o DuckDB 2.0, construído sobre o Chargewire, um protocolo aberto para SQL por HTTP com resultados em Apache Arrow. Em uma única máquina Graviton5, ele executou 22 consultas derivadas do TPC-H sobre 1 TB em ${tpchPt.hcSeconds} segundos, por cerca de ${tpchPt.hcCents} centavos de dólar de computação, e todos os recursos estão incluídos: controle de acesso, SSO, mTLS, auditoria, instrumentação e uma interface web integrada. Apache-2.0, sem chaves de licença.`,
    "image": "/blog/introducing-hardcharger/hero-v2-800w.webp",
    "imageWidth": 1600,
    "imageHeight": 900
  },
  "introducing-gizmodata-cloud": {
    "id": "introducing-gizmodata-cloud",
    "slug": "introducing-gizmodata-cloud",
    "title": "Apresentamos o GizmoData Cloud: a melhor forma de executar DuckDB na nuvem",
    "date": "2026-09-28",
    "readTime": "6 min de leitura",
    "category": "Lançamento",
    "author": "Philip Moore",
    "tags": [
      "GizmoData Cloud",
      "GizmoSQL",
      "DuckDB",
      "DuckLake",
      "Arrow Flight SQL",
      "Serviço gerenciado",
      "AWS",
      "Google Cloud",
      "Azure"
    ],
    "excerpt": "O GizmoData Cloud está disponível para todos. É o GizmoSQL totalmente gerenciado: clusters Arrow Flight SQL com DuckDB, catálogos DuckLake e consultas federadas na AWS, no Google Cloud e no Azure, em um único plano de controle. Cadastre-se em app.gizmodata.com. Os primeiros 20 cadastros com nosso código de lançamento recebem US$ 25 em crédito, sem cartão de crédito.",
    "image": "/gizmodata-cloud-launch-blog.svg",
    "imageWidth": 1600,
    "imageHeight": 900
  },
  "gizmosql-session-limits-idle-timeouts": {
    "id": "gizmosql-session-limits-idle-timeouts",
    "slug": "gizmosql-session-limits-idle-timeouts",
    "title": "A conexão que nunca foi embora: mantenha o GizmoSQL enxuto com limites de sessão e de ociosidade",
    "date": "2026-09-15",
    "readTime": "5 min de leitura",
    "category": "Artigo de convidado",
    "author": "Emmanuel Sibanda",
    "tags": [
      "Artigo de convidado",
      "GizmoSQL",
      "Operações",
      "Sessões",
      "Tempo limite de ociosidade",
      "Memória",
      "DuckDB",
      "Arrow Flight SQL"
    ],
    "excerpt": "Ninguém executou uma consulta desde sexta-feira, mas na segunda-feira de manhã o servidor GizmoSQL está com 80% da memória ocupada. As consultas foram embora; as conexões ficaram. O autor convidado Emmanuel Sibanda explica as duas opções que resolvem isso: --max-sessions limita o número de sessões, e --session-idle-timeout encerra as ociosas, com um relógio reiniciado apenas por SQL real do usuário, incluindo a transmissão de resultados.",
    "originalUrl": "https://www.emmanuelsibanda.com",
    "isGuestArticle": true,
    "image": "/gizmosql-idle-sessions-blog.svg",
    "imageWidth": 1600,
    "imageHeight": 900
  },
  "gizmosql-ducklake-elastic-compute-shared-state": {
    "id": "gizmosql-ducklake-elastic-compute-shared-state",
    "slug": "gizmosql-ducklake-elastic-compute-shared-state",
    "title": "GizmoSQL + DuckLake: computação elástica sobre estado compartilhado",
    "date": "2026-09-11",
    "readTime": "5 min de leitura",
    "category": "Arquitetura",
    "author": "Chris Wones",
    "tags": [
      "DuckLake",
      "GizmoSQL",
      "Arquitetura",
      "Escalonamento automático",
      "Lakehouse",
      "DuckDB",
      "PostgreSQL",
      "Parquet",
      "Kubernetes"
    ],
    "excerpt": "Escalar computação sem estado é fácil. O desafio da análise de dados é escalar nós independentes que precisam dos mesmos dados mutáveis. Com o catálogo transacional no DuckLake e os arquivos Parquet em armazenamento de objetos, um cluster GizmoSQL pode crescer de um para cinco nós e voltar, sem copiar dados, reconstruir metadados ou reconciliar estado. O consultor estratégico Chris Wones explica por que o catálogo simplifica esse escalonamento.",
    "image": "/gizmosql-ducklake-elastic-compute-hero.svg",
    "imageWidth": 1600,
    "imageHeight": 900
  },
  "seagull-scalable-web-gis-gizmosql": {
    "id": "seagull-scalable-web-gis-gizmosql",
    "slug": "seagull-scalable-web-gis-gizmosql",
    "title": "Seagull: como usar o GizmoSQL para criar aplicações GIS web escaláveis",
    "date": "2026-09-08",
    "readTime": "6 min de leitura",
    "category": "Artigo de convidado",
    "author": "Kasper F. Pedersen",
    "tags": [
      "Artigo de convidado",
      "GIS",
      "Marítimo",
      "AIS",
      "DuckDB",
      "GizmoSQL",
      "Arrow Flight SQL",
      "MapLibre",
      "Apache Airflow",
      "Universidade de Aalborg"
    ],
    "excerpt": "Artigo convidado do grupo de pesquisa Daisy, da Universidade de Aalborg. O Seagull apresenta 18,7 bilhões de registros AIS de 126.400 navios em camadas de mapa ao vivo, com cada bloco vetorial calculado sob demanda pelo DuckDB. O PostgreSQL exigia muitas tabelas pré-agregadas; o DuckDB era rápido, mas permitia um único processo de gravação. O GizmoSQL o transformou em um banco centralizado, compartilhado por Airflow, MapLibre Martin e pela aplicação web via Arrow Flight SQL.",
    "originalUrl": "https://seagull.app.cs.aau.dk",
    "isGuestArticle": true,
    "image": "/seagull-gizmosql-blog.svg",
    "imageWidth": 1600,
    "imageHeight": 900
  },
  "tpch-1tb-graviton5-80-seconds": {
    "id": "tpch-1tb-graviton5-80-seconds",
    "slug": "tpch-1tb-graviton5-80-seconds",
    "title": "TPC-H de 1 TB em 80 segundos: GizmoSQL no AWS Graviton5 versus Graviton4 e Azure Cobalt 100",
    "date": "2026-09-04",
    "readTime": "10 min de leitura",
    "category": "Desempenho",
    "author": "Philip Moore",
    "tags": [
      "Benchmark",
      "TPC-H",
      "AWS Graviton5",
      "Graviton4",
      "Graviton",
      "Arm",
      "GizmoSQL",
      "DuckDB",
      "Desempenho"
    ],
    "excerpt": "Executamos nosso benchmark TPC-H de 1 TB e 22 consultas em uma das primeiras instâncias AWS Graviton5, uma r9gd.24xlarge: 80,5 segundos por US$ 0,17 de computação. Na mesma tarde, com o mesmo DuckDB, uma Graviton4 r8gd.24xlarge levou 89,8 segundos e uma Azure Cobalt 100 E96pds_v6 levou 106,8. O Graviton5 foi 10% mais rápido que o Graviton4 e 25% mais rápido que o Cobalt 100, vencendo nas 22 consultas. Veja a metodologia completa e os tempos de cada consulta nas três execuções.",
    "image": "/tpch-1tb-graviton5-blog.svg",
    "imageWidth": 1600,
    "imageHeight": 900
  },
  "introducing-gizmosql-mcp-server": {
    "id": "introducing-gizmosql-mcp-server",
    "slug": "introducing-gizmosql-mcp-server",
    "title": "Apresentamos o servidor MCP do GizmoSQL: converse com seus dados DuckDB pelo Claude",
    "date": "2026-09-04",
    "readTime": "7 min de leitura",
    "category": "Lançamento",
    "author": "Philip Moore",
    "tags": [
      "MCP",
      "Model Context Protocol",
      "Claude",
      "Agentes de IA",
      "GizmoSQL",
      "DuckDB",
      "Arrow Flight SQL"
    ],
    "excerpt": "Lançamos um servidor MCP e uma extensão Claude Desktop para o GizmoSQL. Claude Desktop, Claude Code e qualquer cliente Model Context Protocol podem explorar seu esquema e executar SQL em um servidor GizmoSQL, com acesso somente de leitura por padrão, limites de linhas e de tempo impostos pelo servidor e credenciais que permanecem na sua máquina.",
    "image": "/gizmosql-mcp-blog.svg",
    "imageWidth": 1600,
    "imageHeight": 900
  },
  "gizmodata-adbc-driver-suite": {
    "id": "gizmodata-adbc-driver-suite",
    "slug": "gizmodata-adbc-driver-suite",
    "title": "Conheça a família de drivers ADBC da GizmoData: mova dados entre Oracle, Db2 e GizmoSQL",
    "date": "2026-08-27",
    "readTime": "9 min de leitura",
    "category": "Lançamento",
    "author": "Philip Moore",
    "tags": [
      "ADBC",
      "Oracle",
      "Db2",
      "GizmoSQL",
      "DuckDB",
      "Apache Arrow",
      "Go",
      "Integração de dados"
    ],
    "excerpt": "Criamos drivers ADBC em Go puro, instaláveis por pip, para Oracle (sem Instant Client), IBM Db2 (sem CLI/JCC) e Quack do DuckDB, ao lado do nosso principal driver GizmoSQL. Como todos são nativos Arrow, mover uma tabela de Oracle ou Db2 para GizmoSQL exige poucas linhas de Python ou uma instrução SQL no DuckDB, nas duas direções.",
    "image": "/adbc-drivers-launch-blog.svg",
    "imageWidth": 1600,
    "imageHeight": 900
  },
  "gizmosql-bulk-ingest-arrow-scan-rewrite": {
    "id": "gizmosql-bulk-ingest-arrow-scan-rewrite",
    "slug": "gizmosql-bulk-ingest-arrow-scan-rewrite",
    "title": "A reescrita com Arrow Scan: como o GizmoSQL v1.37.0 tornou a ingestão em massa 7 vezes mais rápida",
    "date": "2026-08-24",
    "readTime": "10 min de leitura",
    "category": "Artigo de convidado",
    "author": "Chris Harrison",
    "tags": [
      "GizmoSQL",
      "Ingestão em massa",
      "Arrow Flight SQL",
      "DuckDB",
      "ADBC",
      "Desempenho",
      "Apache Arrow"
    ],
    "excerpt": "O GizmoSQL v1.37.0 deixou de converter manualmente cada célula Arrow ao entrar no DuckDB e passou a transmitir o lote inteiro pelo arrow_scan do próprio DuckDB: cargas em massa 7,2 vezes mais rápidas, decimais exatos e colunas de geometria que finalmente chegam como GEOMETRY. Veja a análise do commit e um repositório executável para reproduzir os resultados.",
    "originalUrl": "https://github.com/virtualninjach/bulk-ingest-demo",
    "isGuestArticle": true,
    "image": "/gizmosql-bulk-ingest-arrow-scan-blog.svg",
    "imageWidth": 1600,
    "imageHeight": 900
  },
  "gizmosql-adbc-v2-go-driver": {
    "id": "gizmosql-adbc-v2-go-driver",
    "slug": "gizmosql-adbc-v2-go-driver",
    "title": "Driver ADBC do GizmoSQL 2.0: um núcleo em Go leva o GizmoSQL a todas as linguagens ADBC",
    "date": "2026-07-29",
    "readTime": "8 min de leitura",
    "category": "Lançamento",
    "author": "Philip Moore",
    "tags": [
      "ADBC",
      "Go",
      "Python",
      "Arrow Flight SQL",
      "GizmoSQL",
      "OAuth",
      "Lançamento"
    ],
    "excerpt": "O driver ADBC do GizmoSQL foi refeito do zero em Go, com uma camada Python fina que mantém a compatibilidade com a versão 1.x. Um único driver nativo agora oferece OAuth/SSO, detecção automática de DDL/DML e os demais recursos do GizmoSQL em Python, Go, R, Rust, C#, C/C++ e JavaScript.",
    "image": "/gizmosql-adbc-v2-blog.svg",
    "imageWidth": 1600,
    "imageHeight": 900
  },
  "welcoming-quack-duckdb-client-server": {
    "id": "welcoming-quack-duckdb-client-server",
    "slug": "welcoming-quack-duckdb-client-server",
    "title": "Boas-vindas ao Quack: o novo protocolo cliente-servidor do DuckDB e o futuro do GizmoSQL",
    "date": "2026-05-13",
    "readTime": "6 min de leitura",
    "category": "Comentário",
    "author": "Philip Moore",
    "tags": [
      "GizmoSQL",
      "DuckDB",
      "Quack",
      "Arrow Flight SQL",
      "Cliente-servidor"
    ],
    "excerpt": "A DuckDB Labs anunciou o Quack, um protocolo cliente-servidor nativo para DuckDB. Agradecemos à equipe pelo reconhecimento e analisamos o lugar do GizmoSQL em um mundo com vários protocolos de servidor DuckDB.",
    "image": "/welcoming-quack-blog.webp",
    "imageWidth": 1600,
    "imageHeight": 800
  },
  "freeing-individuals-from-the-matrix": {
    "id": "freeing-individuals-from-the-matrix",
    "slug": "freeing-individuals-from-the-matrix",
    "title": "Libertando os indivíduos da Matrix: como o GizmoSQL prepara seu banco para agentes de IA",
    "date": "2026-03-05",
    "readTime": "15 min de leitura",
    "category": "Artigo de convidado",
    "author": "Chris Harrison",
    "tags": [
      "GizmoSQL",
      "Agentes de IA",
      "DuckDB",
      "Claude",
      "Apache Arrow",
      "Python",
      "Windows"
    ],
    "excerpt": "Uma análise inspirada em Matrix sobre como construir uma arquitetura pronta para agentes de IA com GizmoSQL, DuckDB e Claude. O projeto simula um milhão de pessoas, altera seus dados com agentes automatizados e monitora anomalias com uma IA autônoma, tudo pelo Arrow Flight SQL.",
    "originalUrl": "https://github.com/virtualninjach/gizmosql-matrix",
    "isGuestArticle": true,
    "image": "/gizmosql-matrix-blog.webp",
    "imageWidth": 1536,
    "imageHeight": 1024
  },
  "what-the-heck-is-gizmosql": {
    "id": "what-the-heck-is-gizmosql",
    "slug": "what-the-heck-is-gizmosql",
    "title": "Afinal, o que é GizmoSQL?",
    "date": "2025-12-30",
    "readTime": "6 min de leitura",
    "category": "Artigo de convidado",
    "author": "Shawn Gordon",
    "tags": [
      "GizmoSQL",
      "DuckDB",
      "Apache Arrow",
      "Arrow Flight SQL",
      "Código aberto"
    ],
    "excerpt": "Uma análise do GizmoSQL: um pequeno servidor que executa DuckDB envolvido pelo protocolo Arrow Flight SQL, permitindo usá-lo remotamente. Publicado originalmente no HackerNoon.",
    "originalUrl": "https://hackernoon.com/what-the-heck-is-gizmosql",
    "isGuestArticle": true,
    "image": "/what-the-heck-is-gizmosql-blog.webp",
    "imageWidth": 1280,
    "imageHeight": 1211
  },
  "introducing-gizmosql-operator": {
    "id": "introducing-gizmosql-operator",
    "slug": "introducing-gizmosql-operator",
    "title": "Apresentamos o GizmoSQL Operator",
    "date": "2025-12-19",
    "readTime": "5 min de leitura",
    "category": "Artigo de convidado",
    "author": "Nima Khoshini",
    "tags": [
      "GizmoSQL",
      "Kubernetes",
      "Operator",
      "Helm",
      "DevOps",
      "Código aberto"
    ],
    "excerpt": "Anunciamos o GizmoSQL Kubernetes Operator, uma ferramenta para reduzir o custo operacional de consultas SQL em larga escala, com economia relatada de 90% em relação a mecanismos de dados concorrentes na nuvem.",
    "originalUrl": "https://www.nima.blog/blog/introducing-gizmosql-operator",
    "isGuestArticle": true,
    "image": "/gizmosql-operator-blog.webp",
    "imageWidth": 1024,
    "imageHeight": 1024
  },
  "adbc-flightsql-bulk-ingestion": {
    "id": "adbc-flightsql-bulk-ingestion",
    "slug": "adbc-flightsql-bulk-ingestion",
    "title": "ADBC Flight SQL 1.10.0: ingestão em massa para GizmoSQL",
    "date": "2026-01-10",
    "readTime": "6 min de leitura",
    "category": "Lançamento",
    "author": "Equipe GizmoData",
    "tags": [
      "ADBC",
      "Arrow Flight SQL",
      "GizmoSQL",
      "Python",
      "DuckDB",
      "Ingestão em massa"
    ],
    "excerpt": "O novo driver Python ADBC Flight SQL 1.10.0 traz ingestão em massa para servidores Arrow Flight SQL como o GizmoSQL. Carregue milhões de linhas diretamente de Python ou DuckDB, com inferência automática de esquema.",
    "image": "/adbc-bulk-ingestion-blog.webp",
    "imageWidth": 1536,
    "imageHeight": 1024
  },
  "gizmoedge-one-trillion-row-challenge": {
    "id": "gizmoedge-one-trillion-row-challenge",
    "slug": "gizmoedge-one-trillion-row-challenge",
    "title": "GizmoEdge encara o desafio de 1 trilhão de linhas",
    "date": "2025-10-22",
    "readTime": "8 min de leitura",
    "category": "Desempenho",
    "author": "Equipe GizmoData",
    "tags": [
      "GizmoEdge",
      "DuckDB",
      "Big Data",
      "Desempenho",
      "Computação distribuída"
    ],
    "excerpt": "O que acontece quando um mecanismo SQL distribuído recebe um conjunto de um trilhão de linhas? O GizmoEdge superou o desafio da Coiled com um cluster de 1.000 workers no Azure.",
    "image": "/gizmoedge-1trc-blog.webp",
    "imageWidth": 1536,
    "imageHeight": 1024
  },
  "gizmosql-one-trillion-row-challenge": {
    "id": "gizmosql-one-trillion-row-challenge",
    "slug": "gizmosql-one-trillion-row-challenge",
    "title": "Resultados do GizmoSQL no desafio de um trilhão de linhas",
    "date": "2025-07-15",
    "readTime": "5 min de leitura",
    "category": "Desempenho",
    "author": "Equipe GizmoData",
    "tags": [
      "GizmoSQL",
      "DuckDB",
      "Big Data",
      "Desempenho"
    ],
    "excerpt": "O GizmoSQL concluiu o desafio de 1 trilhão de linhas! Saiba como processamos conjuntos de dados enormes usando DuckDB e Apache Arrow Flight SQL.",
    "image": "/gizmosql-1trc-blog.webp",
    "imageWidth": 1536,
    "imageHeight": 1024
  },  "adbc-replicate-snowflake-oracle-ai-agents": {
    "id": "adbc-replicate-snowflake-oracle-ai-agents",
    "slug": "adbc-replicate-snowflake-oracle-ai-agents",
    "title": "Replicar uma vez, consultar o dia todo: adbc-replicate alimenta agentes de IA com dados do Snowflake e do Oracle",
    "date": "2026-10-16",
    "readTime": "11 min de leitura",
    "category": "Lançamento",
    "author": "Philip Moore",
    "tags": [
      "adbc-replicate",
      "Replicação",
      "Snowflake",
      "Oracle",
      "MCP",
      "Agentes de IA",
      "GizmoData Cloud",
      "GizmoSQL",
      "DuckDB",
      "Apache Arrow",
      "ADBC"
    ],
    "excerpt": "O adbc-replicate é nossa nova ferramenta de código aberto para copiar esquemas inteiros do Snowflake, Oracle, PostgreSQL ou DuckDB para o GizmoSQL. Os dados fluem como Arrow de ponta a ponta, as máscaras são aplicadas na origem e cada execução copia somente o que mudou. Dois clientes — um com Snowflake e outro com Oracle — executam a ferramenta a cada hora para o GizmoData Cloud, e seus agentes de IA consultam a cópia via MCP durante todo o dia sem tocar na origem.",
    "image": "/adbc-replicate-blog.svg",
    "imageWidth": 1600,
    "imageHeight": 900
  },
  "tpch-1tb-horse-race-aws-gcp-azure": {
    "id": "tpch-1tb-horse-race-aws-gcp-azure",
    "slug": "tpch-1tb-horse-race-aws-gcp-azure",
    "title": "Três nuvens, uma API: uma corrida TPC-H de 1 TB no GizmoData Cloud (AWS vs. Google Cloud vs. Azure)",
    "date": "2026-10-08",
    "readTime": "9 min de leitura",
    "category": "Desempenho",
    "author": "Philip Moore",
    "tags": [
      "Benchmark",
      "TPC-H",
      "GizmoData Cloud",
      "AWS Graviton5",
      "Google Axion",
      "Azure Cobalt 100",
      "DuckLake",
      "DuckDB 2.0",
      "GizmoSQL",
      "DuckDB",
      "Desempenho"
    ],
    "excerpt": "Colocamos seis clusters GizmoSQL de 64 vCPUs para competir na AWS, no Google Cloud e no Azure com 1 TB de TPC-H, em NVMe local e no DuckLake, usando DuckDB 1.5.6 e a versão alpha do DuckDB 2.0. O AWS Graviton5 fez a volta mais rápida (79 segundos para as 22 consultas, por US$ 0,30), o Google Cloud levou quatro das seis medalhas de ouro e o DuckDB 2.0 superou a versão 1.5.6 em todas as categorias. Foram 12 resultados e 792 execuções, todas com custo registrado, em 38 minutos e por cerca de US$ 37 no GizmoData Cloud.",
    "image": "/blog/tpch-1tb-horse-race/hero-800w.webp",
    "imageWidth": 1600,
    "imageHeight": 901
  }
}
} satisfies Record<'en' | 'pt-BR', Record<string, BlogMetadata>>;
export type BlogSlug = keyof typeof blogMetadata.en;
export const blogPostSlugs = Object.keys(blogMetadata.en) as BlogSlug[];
export const getBlogPosts = (locale: 'en'|'pt-BR'): BlogMetadata[] => Object.values(blogMetadata[locale]);
